import { useEffect, useId, useRef, useState } from 'react';
import { findTrustedLocations, validSubjectZip, type TrustedLocation } from '../../lib/trusted-locations';

export function CeremonyLocationPicker({ selected, onSelect, disabled }: {
  selected: TrustedLocation | null; onSelect: (location: TrustedLocation | null) => void; disabled: boolean;
}) {
  const id = useId();
  const [zip, setZip] = useState('');
  const [results, setResults] = useState<TrustedLocation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);

  async function search() {
    if (disabled) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    onSelect(null); setResults(null); setError(null); setLoading(true);
    try {
      const locations = await findTrustedLocations(zip, controller.signal);
      if (!controller.signal.aborted) setResults(locations);
    } catch (reason) {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Locations could not be loaded.');
    } finally { if (!controller.signal.aborted) setLoading(false); }
  }

  return <fieldset disabled={disabled} className="space-y-3">
    <legend className="mb-2 text-sm font-medium">Choose a ceremony location</legend>
    <label htmlFor={id} className="block text-sm">Subject’s ZIP</label>
    <div className="flex flex-wrap items-center gap-2">
      <input id={id} value={zip} type="text" inputMode="numeric" autoComplete="off" maxLength={10}
        placeholder="e.g. 78704" className="w-36 rounded border border-neutral-300 bg-white px-3 py-2"
        onChange={event => {
          request.current?.abort(); setLoading(false); setZip(event.target.value);
          setResults(null); setError(null); onSelect(null);
        }}
        onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); void search(); } }} />
      <button type="button" disabled={disabled || loading || !validSubjectZip(zip)} onClick={() => void search()}
        className="rounded border border-neutral-300 bg-white px-4 py-2 text-sm font-medium disabled:opacity-50">
        {loading ? 'Finding locations…' : 'Find locations'}
      </button>
    </div>
    {loading && <p role="status" className="text-sm text-neutral-600">Looking for locations near the subject’s ZIP…</p>}
    {error && <p role="alert" className="text-sm text-red-800">{error}</p>}
    {results?.length === 0 && <p role="status" className="text-sm text-neutral-600">No locations found nearby. Try another ZIP or enter the delivery email manually.</p>}
    {results && results.length > 0 && <div className="space-y-2">
      <p className="text-sm text-neutral-600">Select where the subject will complete the ceremony.</p>
      {results.map(location => <label key={location.storeNumber}
        className={`flex cursor-pointer gap-3 rounded border p-4 ${selected?.storeNumber === location.storeNumber ? 'border-neutral-900 bg-white' : 'border-neutral-200 bg-white'}`}>
        <input type="radio" name={`${id}-location`} value={location.storeNumber} className="mt-1"
          checked={selected?.storeNumber === location.storeNumber} disabled={disabled || !location.emailToPrint}
          onChange={() => onSelect(location)} />
        <span className="min-w-0 text-sm">
          <span className="block font-medium">{location.name}</span>
          <span className="block text-neutral-600">{location.street}, {location.city}, {location.state} {location.zip}</span>
          <span className="mt-1 block text-neutral-600">{location.distanceText}{location.durationText ? ` · ${location.durationText} drive` : ''}</span>
          <span className={`block ${location.isOpenNow ? 'text-emerald-800' : 'text-amber-800'}`}>{location.openHint}</span>
          {!location.emailToPrint && <span className="block text-amber-800">Document delivery is unavailable for this location.</span>}
        </span>
      </label>)}
    </div>}
    {selected && <p role="status" className="break-words text-sm text-neutral-600">Document delivery: {selected.emailToPrint}</p>}
  </fieldset>;
}
